import { useCollection } from "@/features/collections/useCollection";
import {
  CollectionToolbar,
  CollectionPagination,
} from "@/features/collections/CollectionControls";
import { useMemo, useState, type ReactNode } from "react";
import { Pencil, Trash2 } from "lucide-react";
import type { CoachingVisibility, Match, MatchSummary } from "@/lib/types";
import { useMatches } from "@/features/matches/useMatches";
import {
  useCoachSummariesList,
  useMatchSummaryMutations,
} from "./hooks/useCoachSummariesList";
import { MatchSummaryComposerModal } from "./components/MatchSummaryComposerModal";
import { PageHeader } from "./components/PageHeader";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Button } from "@/components/ui/Button";
import { useToast } from "@/components/ui/toast";
import { errorMessage } from "@/lib/api";
import { linkedEvidenceLabel, visibilityLabel } from "./feedbackLibraryLabels";
import { Badge } from "@/components/ui/Badge";
import { BulkSelectionBar, SelectToggle } from "./components/BulkSelectionBar";
import { CoachModal } from "./components/CoachModal";
import { VISIBILITY_LABELS } from "./visibilityVocabulary";
import { MatchRecapThumbnail } from "@/components/MatchRecapThumbnail";
import { useQuery } from "@tanstack/react-query";
import { getAppSettings } from "@/lib/api";
import { publicAppSettingsKey } from "@/lib/queryClient";
import { useScopeStore } from "@/stores/scope";

/**
 * Coach > Match Summaries list page (M6a).
 *
 * Ports the legacy `renderCoachMatchSummaries`
 * (`js/coaching/match-summaries.js`): the team's match summaries,
 * each with the match label, visibility + linked-evidence counts,
 * a short recap preview, and per-row Edit / Delete.
 *
 * Like Playlists, the **composer is its own modal**
 * (`<MatchSummaryComposerModal>`) — there's no Coach Review
 * surface for summaries. "+ New summary" opens it in create mode.
 */

export function MatchSummariesPage() {
  const toast = useToast();
  const summariesQuery = useCoachSummariesList();
  const matchesQuery = useMatches();
  const settingsQuery = useQuery({
    queryKey: publicAppSettingsKey(),
    queryFn: getAppSettings,
    staleTime: 60_000,
  });
  const tenantTeamName = useScopeStore((state) => state.active?.team.name);
  const mutations = useMatchSummaryMutations();

  const summaries = summariesQuery.data?.summaries ?? [];
  const matchById = useMemo(
    () => new Map((matchesQuery.data ?? []).map((match) => [match.id, match])),
    [matchesQuery.data],
  );

  const matchLabelById = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of matchesQuery.data ?? []) {
      const teams = `${m.home_team} vs ${m.away_team}`;
      map.set(m.id, m.date ? `${teams} · ${m.date}` : teams);
    }
    return map;
  }, [matchesQuery.data]);

  const collection = useCollection(summaries, {
    scope: "MatchSummariesPage.tsx",
    text: (item) =>
      `${matchLabelById.get(item.match_id) ?? item.match_id} ${item.team_positives} ${item.team_improvements} ${item.training_focus}`,
    matches: (item, visibility) => item.visibility === visibility,
  });
  const [deleting, setDeleting] = useState<number | null>(null);
  // `composer`: null = closed; { summary: null } = create;
  // { summary } = edit.
  const [composer, setComposer] = useState<{
    summary: MatchSummary | null;
  } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<MatchSummary | null>(null);
  const [previewSummary, setPreviewSummary] = useState<MatchSummary | null>(
    null,
  );
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  const selectedSummaries = useMemo(
    () => summaries.filter((summary) => selected.has(summary.id)),
    [selected, summaries],
  );

  const toggleSelected = (id: number) =>
    setSelected((previous) => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const handleBulkVisibility = async (visibility: CoachingVisibility) => {
    if (selectedSummaries.length === 0) return;
    setBulkBusy(true);
    try {
      const results = await Promise.allSettled(
        selectedSummaries.map((summary) =>
          mutations.updateSummary(summary.id, { visibility }),
        ),
      );
      const updated = results.filter(
        (result) => result.status === "fulfilled",
      ).length;
      const failed = results.length - updated;
      if (failed === 0) {
        toast.success(
          `Updated visibility on ${updated} recap${updated === 1 ? "" : "s"}`,
        );
      } else {
        toast.error(`Updated ${updated}, failed ${failed}.`);
      }
      setSelected(new Set());
    } catch (err) {
      toast.error("Couldn't update recap visibility", {
        description: errorMessage(err),
      });
    } finally {
      setBulkBusy(false);
    }
  };

  const confirmBulkDeleteSummaries = async () => {
    setConfirmBulkDelete(false);
    if (selectedSummaries.length === 0) return;
    setBulkBusy(true);
    try {
      const results = await Promise.allSettled(
        selectedSummaries.map((summary) => mutations.deleteSummary(summary.id)),
      );
      const deleted = results.filter(
        (result) => result.status === "fulfilled",
      ).length;
      const failed = results.length - deleted;
      if (failed === 0) {
        toast.success(`Deleted ${deleted} recap${deleted === 1 ? "" : "s"}`);
      } else {
        toast.error(`Deleted ${deleted}, failed ${failed}.`);
      }
      setSelected(new Set());
    } catch (err) {
      toast.error("Couldn't delete recaps", { description: errorMessage(err) });
    } finally {
      setBulkBusy(false);
    }
  };

  const handleDelete = (summary: MatchSummary) => {
    setConfirmDelete(summary);
  };

  const confirmDeleteSummary = async () => {
    if (!confirmDelete) return;
    const summary = confirmDelete;
    setConfirmDelete(null);
    setDeleting(summary.id);
    try {
      await mutations.deleteSummary(summary.id);
      toast.success("Match recap deleted");
    } catch (err) {
      toast.error("Couldn't delete match recap", {
        description: errorMessage(err),
      });
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="flex flex-col gap-5" data-testid="summaries-page">
      {confirmDelete ? (
        <ConfirmDialog
          title="Delete summary?"
          message={`Delete this match recap? This can't be undone.`}
          confirmLabel="Delete"
          danger
          initialFocus="close"
          onConfirm={confirmDeleteSummary}
          onClose={() => setConfirmDelete(null)}
          data-testid="summaries-delete-confirm"
        />
      ) : null}
      {confirmBulkDelete ? (
        <ConfirmDialog
          title="Delete selected recaps?"
          message={`Delete ${selectedSummaries.length} recap${selectedSummaries.length === 1 ? "" : "s"}? This can't be undone.`}
          confirmLabel="Delete"
          danger
          initialFocus="close"
          onConfirm={confirmBulkDeleteSummaries}
          onClose={() => setConfirmBulkDelete(false)}
          data-testid="summaries-bulk-delete-confirm"
        />
      ) : null}
      <PageHeader
        title="Match Recaps"
        description="Team-level feedback that turns a match into positives, improvements, training focus, and linked evidence."
        actions={
          <Button
            type="button"
            onClick={() => setComposer({ summary: null })}
            data-testid="summaries-new-summary"
            aria-label="New match recap"
            title="New match recap"
            size="sm"
            className="library-create-action"
          >
            New match recap
          </Button>
        }
      />

      <CollectionToolbar
        collection={collection}
        label="recaps"
        searchPlaceholder="Title, player, match or category"
        filterLabel="Visibility"
        filters={[
          { value: "", label: "All audiences" },
          { value: "team", label: "Team" },
          { value: "player", label: "Player + family" },
          { value: "coach", label: "Coaches" },
        ]}
      />
      {collection.controls && collection.filteredTotal === 0 ? (
        <p className="text-sm text-ink-muted">
          No results. Clear filters to see all items.
        </p>
      ) : null}
      <BulkSelectionBar
        count={selectedSummaries.length}
        busy={bulkBusy}
        onSetVisibility={handleBulkVisibility}
        onDelete={() => setConfirmBulkDelete(true)}
        onClear={() => setSelected(new Set())}
        testIdPrefix="summaries"
      />
      {summariesQuery.isLoading ? (
        <p
          className="text-ink-muted text-sm font-mono px-3 py-6"
          data-testid="summaries-loading"
        >
          Loading summaries…
        </p>
      ) : summaries.length === 0 ? (
        <p
          className="text-ink-muted text-sm px-3 py-6"
          data-testid="summaries-empty"
        >
          No match recaps yet. Add a team-visible recap after a match.
        </p>
      ) : (
        <ul className="flex flex-col gap-2" data-testid="summaries-list">
          {collection.items.map((summary) => (
            <SummaryRow
              key={summary.id}
              summary={summary}
              match={matchById.get(summary.match_id)}
              clubLogoUrl={settingsQuery.data?.assets?.club_logo_url}
              tenantTeamName={tenantTeamName}
              matchLabel={
                matchLabelById.get(summary.match_id) ?? summary.match_id
              }
              deleting={deleting === summary.id}
              selected={selected.has(summary.id)}
              onToggleSelected={() => toggleSelected(summary.id)}
              onOpen={() => setPreviewSummary(summary)}
              onEdit={() => setComposer({ summary })}
              onDelete={() => handleDelete(summary)}
            />
          ))}
        </ul>
      )}

      <CollectionPagination collection={collection} />

      {composer ? (
        <MatchSummaryComposerModal
          summary={composer.summary}
          mutations={mutations}
          onClose={() => setComposer(null)}
        />
      ) : null}
      {previewSummary ? (
        <MatchSummaryDetailModal
          summary={previewSummary}
          matchLabel={
            matchLabelById.get(previewSummary.match_id) ??
            previewSummary.match_id
          }
          onClose={() => setPreviewSummary(null)}
          onEdit={() => {
            setPreviewSummary(null);
            setComposer({ summary: previewSummary });
          }}
        />
      ) : null}
    </div>
  );
}

type SummaryRowProps = {
  summary: MatchSummary;
  match?: Match;
  clubLogoUrl?: string | null;
  tenantTeamName?: string | null;
  matchLabel: string;
  deleting: boolean;
  selected: boolean;
  onToggleSelected: () => void;
  onOpen: () => void;
  onEdit: () => void;
  onDelete: () => void;
};

function SummaryRow({
  summary,
  match,
  clubLogoUrl,
  tenantTeamName,
  matchLabel,
  deleting,
  selected,
  onToggleSelected,
  onOpen,
  onEdit,
  onDelete,
}: SummaryRowProps) {
  const noteCount = summary.note_ids?.length ?? 0;
  const clipCount = summary.clip_ids?.length ?? 0;
  const playlistCount = summary.playlist_ids?.length ?? 0;
  const metaParts = [
    "For: team",
    `Player sees: ${visibilityLabel(summary.visibility)}`,
    linkedEvidenceLabel({
      notes: noteCount,
      clips: clipCount,
      playlists: playlistCount,
    }),
    "Next: Edit recap",
  ];

  const preview =
    (summary.team_positives ?? "").trim() ||
    (summary.team_improvements ?? "").trim() ||
    (summary.training_focus ?? "").trim() ||
    (summary.body ?? "").trim();
  const [matchTitle, matchDate] = matchLabel.split(" · ");

  return (
    <li
      className="summary-library-row grid gap-2 rounded border border-rule bg-surface p-2 shadow-sm sm:grid-cols-[auto_108px_minmax(0,1fr)_auto] sm:items-center"
      data-testid="summary-row"
      data-summary-id={summary.id}
    >
      <SelectToggle
        selected={selected}
        onToggle={onToggleSelected}
        label={selected ? `Deselect ${matchTitle}` : `Select ${matchTitle}`}
        testid="summary-select-toggle"
      />
      <MatchRecapThumbnail
        match={match}
        clubLogoUrl={clubLogoUrl}
        tenantTeamName={tenantTeamName}
        className="h-20 w-[108px]"
        testId="summary-library-thumb"
      />
      <div className="min-w-0 flex-1">
        <p className="eyebrow library-audience">{matchTitle}</p>
        <h3 className="library-item-title">{matchTitle}</h3>
        {preview ? <p className="library-item-description">{preview}</p> : null}
        <div className="library-item-meta">
          <Badge variant="muted">{visibilityLabel(summary.visibility)}</Badge>
          <Badge variant="accent">Recap</Badge>
          {matchDate ? <span>{matchDate}</span> : null}
          <span>
            {linkedEvidenceLabel({
              notes: noteCount,
              clips: clipCount,
              playlists: playlistCount,
            })}
          </span>
        </div>
        <span className="sr-only">{metaParts.join(" · ")}</span>
      </div>
      <div className="library-row-actions">
        <Button
          variant="outline"
          onClick={onOpen}
          data-testid="summary-action-open"
        >
          Open
        </Button>
        <RowButton
          label="Edit"
          testid="summary-action-edit"
          onClick={onEdit}
          icon={<Pencil size={16} strokeWidth={2} aria-hidden="true" />}
        />
        <RowButton
          label="Delete"
          testid="summary-action-delete"
          onClick={onDelete}
          disabled={deleting}
          danger
          icon={<Trash2 size={16} strokeWidth={2} aria-hidden="true" />}
        />
      </div>
    </li>
  );
}

function MatchSummaryDetailModal({
  summary,
  matchLabel,
  onClose,
  onEdit,
}: {
  summary: MatchSummary;
  matchLabel: string;
  onClose: () => void;
  onEdit: () => void;
}) {
  const sections = [
    ["Team positives", summary.team_positives],
    ["Areas to improve", summary.team_improvements],
    ["Training focus", summary.training_focus],
    ["Coach recap", summary.body],
  ] as const;
  return (
    <CoachModal
      title={matchLabel.split(" · ")[0]}
      size="wide"
      onClose={onClose}
      data-testid="summary-detail-modal"
      footer={
        <div className="flex justify-end">
          <Button
            type="button"
            onClick={onEdit}
            data-testid="summary-detail-edit"
          >
            Edit
          </Button>
        </div>
      }
    >
      <div className="summary-detail-layout grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(18rem,0.82fr)]">
        <div className="grid content-start gap-4">
          {sections.map(([label, value]) =>
            value?.trim() ? (
              <section key={label}>
                <h3 className="font-display text-lg text-ink">{label}</h3>
                <p className="mt-1 whitespace-pre-line text-base text-ink">
                  {value}
                </p>
              </section>
            ) : null,
          )}
        </div>
        <aside className="grid content-start gap-3">
          <div className="flex flex-wrap gap-2">
            <Badge variant="accent">Recap</Badge>
            <Badge variant="muted">Team feedback</Badge>
          </div>
          <p className="rounded bg-accent-soft px-4 py-3 text-sm text-ink">
            Shared with: {summaryVisibilitySentence(summary.visibility)}
          </p>
          <span className="sr-only">
            {VISIBILITY_LABELS[summary.visibility]}
          </span>
        </aside>
      </div>
    </CoachModal>
  );
}

function summaryVisibilitySentence(visibility: CoachingVisibility): string {
  if (visibility === "team") {
    return "Everyone on the team, their families, and the coaching staff.";
  }
  if (visibility === "player") {
    return "Tagged players, their families, and the coaching staff.";
  }
  return "The coaching staff only. Players and families never see it.";
}

function RowButton(props: {
  label: string;
  testid: string;
  icon: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={props.onClick}
      disabled={props.disabled}
      data-testid={props.testid}
      title={props.label}
      aria-label={props.label}
      className={[
        "inline-flex h-7 w-7 shrink-0 items-center justify-center rounded border transition-colors disabled:opacity-40",
        props.danger
          ? "border-rule text-ink-muted hover:border-destructive hover:bg-destructive/10 hover:text-destructive"
          : "border-rule text-ink-muted hover:border-accent hover:bg-accent/10 hover:text-accent",
      ].join(" ")}
    >
      <span className="library-action-icon">{props.icon}</span>
      <span className="library-action-label">
        {props.label.replace(/^\+ /, "")}
      </span>
    </button>
  );
}
